import { useMemo } from "react";

import { useAvailableCustomMetrics } from "./useAvailableCustomMetrics";

import {
  IMenuSelection,
  menuSelectionSortList,
  menuSelectionSortSimilarityList,
  MenuSelectionSortType,
} from "@/stores/selectionSort";

/**
 * Builds the sort menus, appending the organization's custom metrics.
 * @param options - Forwarded to {@link useAvailableCustomMetrics}; pass
 *   `enabled: false` while the sort surface is closed (SE-8832)
 * @param options.enabled - Whether the caller needs the custom metrics now
 * @returns The watchlist and similarity sort menus and the custom options
 */
export const useDynamicSortOptions = (options?: {
  readonly enabled?: boolean;
}) => {
  const { availableCustomMetrics, hasCustomMetrics } =
    useAvailableCustomMetrics(options);

  // Create custom metric sort options
  const customMetricSortOptions = useMemo((): IMenuSelection[] => {
    return availableCustomMetrics.map(metric => ({
      title: metric.displayName,
      type: `custom_${metric.id}` as MenuSelectionSortType,
    }));
  }, [availableCustomMetrics]);

  // Enhanced menu selection sort list with custom metrics
  const enhancedMenuSelectionSortList = useMemo((): IMenuSelection[] => {
    return [...menuSelectionSortList, ...customMetricSortOptions];
  }, [customMetricSortOptions]);

  // Similarity sort list - custom metrics NOT supported by listSimilarPlayers API
  // The API only accepts SimilarPlayerSortField enum values, not custom stat names
  const enhancedMenuSelectionSortSimilarityList =
    useMemo((): IMenuSelection[] => {
      return menuSelectionSortSimilarityList;
    }, []);

  return {
    enhancedMenuSelectionSortList,
    enhancedMenuSelectionSortSimilarityList,
    customMetricSortOptions,
    hasCustomMetrics,
  };
};
